// react
import { ReactNode } from 'react';

// mui
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';

export interface DetailRowProps {
  label: string;
  /** IDs have no spaces, so they may break anywhere to fit a phone. */
  isID?: boolean;
  children: ReactNode;
}

export const DetailRow = ({
  label,
  isID = false,
  children,
}: DetailRowProps) => (
  <>
    <Typography variant='body2' color='text.secondary'>
      {label}
    </Typography>
    <Typography
      variant='body2'
      component='div'
      sx={isID ? { wordBreak: 'break-all' } : undefined}
    >
      {children}
    </Typography>
  </>
);

/** DetailList lays out label and value rows in a card. */
export const DetailList = ({ children }: { children: ReactNode }) => (
  <Card>
    <CardContent>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'max-content 1fr' },
          columnGap: 3,
          rowGap: { xs: 0.5, sm: 1 },
        }}
      >
        {children}
      </Box>
    </CardContent>
  </Card>
);

export interface DetailSectionProps {
  title: string;
  /** Buttons beside the title. */
  actions?: ReactNode;
  children: ReactNode;
}

export const DetailSection = ({
  title,
  actions,
  children,
}: DetailSectionProps) => (
  <Stack spacing={1.5}>
    <Stack
      direction='row'
      spacing={1}
      useFlexGap
      sx={{
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      }}
    >
      <Typography variant='h6'>{title}</Typography>
      {actions && (
        <Stack direction='row' spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
          {actions}
        </Stack>
      )}
    </Stack>
    {children}
  </Stack>
);

DetailRow.displayName = 'DetailRow';
DetailSection.displayName = 'DetailSection';
DetailList.displayName = 'DetailList';
export default DetailList;
